export class AccordionItem {
	constructor(domNode) {
		this.rootEl = domNode;
		this.buttonEl = this.rootEl.querySelector("button[aria-expanded]");

		const controlsId = this.buttonEl.getAttribute("aria-controls");
		this.contentEl = document.getElementById(controlsId);

		this.isOpen = this.buttonEl.getAttribute("aria-expanded") === "true";
		this.isDisabled = this.isDisabled();
		if (this.isDisabled) this.disable();

		this.transition = this.rootEl.dataset.transition ? this.rootEl.dataset.transition : "all 0.3s ease";
		this.rootEl.style.setProperty("--accordion-transition", this.transition);

		// add event listeners
		this.clickHandler = this.onButtonClick.bind(this);
		this.buttonEl.addEventListener("click", this.clickHandler);

		if (domNode.dataset.accordionActive == "true") {
			this.open();
		}
	}

	isOpen() {
		return this.isOpen;
	}

	isDisabled() {
		return this.buttonEl.hasAttribute("disabled");
	}

	disable() {
		this.buttonEl.setAttribute("disabled", "");
		this.rootEl.classList.add("opacity-40");
	}

	enable() {
		this.buttonEl.removeAttribute("disabled");
		this.rootEl.classList.remove("opacity-40");
	}

	onButtonClick() {
		this.triggerEvent("accordionClick", {
			accordion: this,
		});

		this.toggle(!this.isOpen);
	}

	toggle(open) {
		// don't do anything if the open state doesn't change
		if (open === this.isOpen) {
			return;
		}

		// update the internal state
		this.isOpen = open;

		// handle DOM updates
		this.buttonEl.setAttribute("aria-expanded", `${open}`);
		if (open) {
			// Fire open event
			this.triggerEvent("accordionOpen", {
				accordion: this,
			});

			this.rootEl.classList.add("active");

			// Handle button
			this.buttonEl.classList.add("active");

			// Handle content
			this.contentEl.removeAttribute("hidden");
			this.contentEl.style.transition = this.transition;
			this.contentEl.style.maxHeight = this.contentEl.scrollHeight + "px";
		} else {
			// Fire close event
			this.triggerEvent("accordionClose", {
				accordion: this,
			});

			this.rootEl.classList.remove("active");

			// Handle button
			this.buttonEl.classList.remove("active");

			// Handle content
			this.contentEl.style.maxHeight = null;
			this.contentEl.addEventListener(
				"transitionend",
				() => {
					this.contentEl.setAttribute("hidden", "");
				},
				{ once: true }
			);
		}
	}

	// Add public open and close methods for convenience
	open() {
		this.toggle(true);
	}

	close() {
		this.toggle(false);
	}

	triggerEvent(name, data) {
		const event = new CustomEvent(name, {
			bubbles: true,
			detail: data,
		});

		this.rootEl.dispatchEvent(event);
	}
}

export class AccordionGroup {
	constructor(domNode, options = {}) {
		this.rootEl = domNode;
		this.rootEl.classList.add("accordion-group");
		this.accordionItems = [];
		this.activeItem = null;
		this.options = {
			disableClose: true,
			...options,
		};

		// Init the items
		this.rootEl.querySelectorAll("[data-accordion]").forEach((accordionEl) => {
			const accordionItem = new AccordionItem(accordionEl);
			this.accordionItems.push(accordionItem);
		});

		// Add event listeners
		this.accordionItems.forEach((accordionItem) => {
			accordionItem.rootEl.addEventListener("accordionOpen", this.handleOpen.bind(this, accordionItem));
			accordionItem.rootEl.addEventListener("accordionClose", this.handleClose.bind(this, accordionItem));

			accordionItem.buttonEl.addEventListener("click", this.closeAll.bind(this, accordionItem));
		});
	}

	handleOpen(accordionItem, e) {
		this.triggerEvent("accordionGroupOpen", {
			group: this,
			accordion: accordionItem,
		});

		this.activeItem = accordionItem;

		if (this.options.disableClose) {
			this.disableClose(accordionItem);
		}
	}

	handleClose(accordionItem, e) {
		this.triggerEvent("accordionGroupClose", {
			group: this,
			accordion: accordionItem,
		});

		if (this.options.disableClose) {
			this.enableClose(accordionItem);
		}
	}

	enableClose(accordionItem) {
		// Re-add the event listener so the accordion can be opened once it has been closed
		accordionItem.buttonEl.addEventListener("click", accordionItem.clickHandler);
	}

	disableClose(accordionItem) {
		// Remove the event listener so the accordion can't be closed by clicking on itself
		// This is needed as the accordion button has items inside which can be clicked
		accordionItem.buttonEl.removeEventListener("click", accordionItem.clickHandler);
	}

	closeAll(accordionItem, e) {
		this.accordionItems.forEach((item) => {
			if (item !== accordionItem) {
				item.close();
			}
		});
	}

	openAll() {
		this.accordionItems.forEach((accordionItem) => {
			accordionItem.open();
		});
	}

	triggerEvent(name, data) {
		const event = new CustomEvent(name, {
			bubbles: true,
			detail: data,
		});

		this.rootEl.dispatchEvent(event);
	}
}

// Init accordions
export function initAccordions() {
	const accordions = document.querySelectorAll("[data-accordion]");
	accordions.forEach((accordionEl) => {
		if (accordionEl.parentElement.hasAttribute("data-accordion-group")) return;
		if (!(accordionEl instanceof AccordionItem)) {
			new AccordionItem(accordionEl);
		}
	});

	const accordionGroups = document.querySelectorAll("[data-accordion-group]");
	accordionGroups.forEach((accordionGroup) => {
		if (!(accordionGroup instanceof AccordionGroup)) {
			new AccordionGroup(accordionGroup);
		}
	});

	const accordionMenus = document.querySelectorAll(".accordion__menu");
	accordionMenus.forEach((accordionMenu) => {
		accordionMenu.querySelectorAll(".menu-item-has-children").forEach((menuItem) => {
			const accordionItem = new AccordionItem(menuItem);
			const accordionTrigger = menuItem.querySelector("button");
			const subMenu = menuItem.querySelector(".sub-menu");

			subMenu.querySelectorAll(".menu-item *").forEach((subMenuItem) => {
				if (subMenuItem.classList.contains("active")) {
					accordionItem.open();
				}
			});
		});
	});
}

initAccordions();
